import { Component, Input, OnInit } from "@angular/core";
import { Network } from "@modules/+new/payments/features/crypto-deposit/domain/entities/network.model";
import { PaymentsCryptocoin } from "@modules/+new/payments/shared-payments/entities/payments-cryptocoin.model";
import { ModalService } from "@core-services/modal.service";
import { MultichainContactsService } from "@modules/main/components/step-summary/sending-modal/services/multichain-contacts.service";
import { config } from "@config";
import { TranslationService } from "@core-services/translation.service";
import { MultichainContact } from "@modules/main/models/multichain-contact.interface";
import { combineLatest, EMPTY, Observable } from "rxjs";
import { DestinationTagType } from "@modules/+new/payments/features/crypto-deposit/domain/entities/destination-tag-type";
import { MultichainContactField } from "@modules/main/models/multichain-contact-field";
import { catchError, filter, map } from "rxjs/operators";
import { SendTransaction } from "@models/transaction";
import { Helper } from "@helpers/functions";
import { SendAmountComponent } from "@modules/main/components/step-summary/sending-modal/send-amount/send-amount.component";
import { SendTrackingFacade } from "@modules/main/components/step-summary/sending-modal/services/send-tracking.facade";
import {
	AddressBookWalletModel,
	AddressBookWalletViewType
} from "@modules/main/components/address-book-wallet/model/address-book-wallet.model";
import { AddressBookWalletFacade } from "@modules/main/components/address-book-wallet/services/address-book-wallet.facade";
import { LaunchDarklyService } from "@core-services/launch-darkly/launch-darkly.service";
import { AddressBookWalletService } from "@modules/main/components/address-book-wallet/services/address-book-wallet.service";

interface PageData {
	wallets: AddressBookWalletModel[];
}

@Component({
	selector: "send-select-recipient",
	templateUrl: "./send-select-recipient.component.html",
	styleUrls: ["./send-select-recipient.component.scss"],
	providers: [MultichainContactsService],
	standalone: false
})
export class SendSelectRecipientComponent implements OnInit {
	@Input() selectedNetwork: Network = null;
	@Input() selectedCryptocoin: PaymentsCryptocoin = null;
	@Input() accountWalletId: string = "";

	public sendTooltipExternalPath =
		this.translationService.getHdLink() + config.externalPaths["send"]["tooltip_contact"];
	public contacts$: Observable<MultichainContact[]> = EMPTY;
	public filteredContacts$: Observable<MultichainContact[]> = EMPTY;
	public recentContacts$: Observable<MultichainContact[]> = EMPTY;
	public filteredRecentContacts$: Observable<MultichainContact[]> = EMPTY;
	public wallets$: Observable<AddressBookWalletModel[]> = EMPTY;
	public destinationTag = "";
	public destinationTagValue = "";
	public recAddress = "";
	public readonly addressBookWalletViewType = AddressBookWalletViewType;
	public query = "";
	public data$: Observable<PageData>;

	constructor(
		private readonly modalService: ModalService,
		private readonly multichainContactsService: MultichainContactsService,
		private readonly translationService: TranslationService,
		private readonly trackingFacade: SendTrackingFacade,
		private readonly walletFacade: AddressBookWalletFacade,
		private readonly launchDarkly: LaunchDarklyService,
		private readonly walletService: AddressBookWalletService
	) {}

	ngOnInit(): void {
		this.destinationTag = this.getDestinationTagLabel(this.selectedNetwork?.destination_tag?.type);
		this.filteredContacts$ = this.contacts$;
		this.recentContacts$ = this.getRecentContacts();
		this.filteredRecentContacts$ = this.recentContacts$;
		this.trackingFacade.trackRecipientScreenViewed();
		this.walletService.shouldRefreshWallets.pipe(filter(Boolean)).subscribe(() => this.refreshWallets());
		this.refreshWallets();
	}

	public onBackButtonClick(): void {
		this.modalService.pop();
	}

	public onCancelButtonClick(): void {
		this.modalService.popAll();
	}

	public addressChange(value: string): void {
		this.filteredContacts$ = this.filterContactsOnAddressAndName(value, this.contacts$);
		this.filteredRecentContacts$ = this.filterContactsOnAddressAndName(value, this.recentContacts$);
	}

	public destinationTagChange(value: string): void {
		this.destinationTagValue = value;
	}

	public clickedItem(field: MultichainContactField, contactName: string): void {
		this.recAddress = field.value;
		this.destinationTagValue = field.destination_tag;
		this.onNext(contactName);
	}

	public addNewWallet(): void {
		this.walletFacade.displayCreateWalletModal(
			AddressBookWalletViewType.WITHDRAW,
			{
				draft: false,
				id: "",
				user_id: "",
				user_owned: false,
				wallet_address: "",
				wallet_host_type: undefined,
				network: this.selectedNetwork,
				asset: this.selectedCryptocoin
			},
			this.accountWalletId,
			this.selectedNetwork,
			this.selectedCryptocoin
		);
	}

	public onNext(contactName: string): void {
		this.trackingFacade.trackRecipientChosen();
		const sendTransaction = new SendTransaction();
		sendTransaction.setWalletId(this.accountWalletId);
		sendTransaction.setRecipient(this.recAddress);
		sendTransaction.setDestinationTag(this.destinationTagValue);
		sendTransaction.setSendType("external");
		sendTransaction._try_internal = false;
		sendTransaction.setIsExternal(true);
		sendTransaction.coin_network_id = this.selectedNetwork.coin_network_id;
		this.multichainContactsService
			.validateContactAndGetFee(this.accountWalletId, sendTransaction)
			.pipe(
				map((res) => {
					const modal = this.modalService.create(SendAmountComponent, {
						selectedNetwork: this.selectedNetwork,
						selectedCryptocoin: this.selectedCryptocoin,
						sendTransaction: sendTransaction,
						contactName: contactName,
						fee: res
					});
					modal.present();
				}),
				catchError((err) => {
					Helper.handleError(err, sendTransaction, { forceToast: true });
					return undefined;
				})
			)
			.subscribe();
	}

	private getDestinationTagLabel(destinationTagType: string): string {
		if (destinationTagType === DestinationTagType.MESSAGE) {
			return "xem_destination_tag";
		}
		if (destinationTagType === DestinationTagType.MEMO) {
			return "stellar_memo";
		}
		return "destination_tag";
	}

	refreshWallets(): void {
		this.wallets$ = this.walletFacade.getWallets().pipe(
			map((wallets: AddressBookWalletModel[]) => {
				return wallets.filter(({ asset, network }: AddressBookWalletModel) => {
					return asset.id === this.selectedCryptocoin.pid && network.id === this.selectedNetwork.id;
				});
			})
		);
		this.data$ = combineLatest([this.wallets$]).pipe(map(([wallets]) => ({ wallets })));
	}

	private filterContactsOnAddressAndName(
		currentRecipientAddressInput: string,
		listToFilter: Observable<MultichainContact[]>
	): Observable<MultichainContact[]> {
		return listToFilter.pipe(
			map((list) => {
				return list
					.filter((item) =>
						item.fields.some((field) => {
							return (
								field.value.startsWith(currentRecipientAddressInput) ||
								item.name.toLowerCase().startsWith(currentRecipientAddressInput.toLowerCase())
							);
						})
					)
					.map((contact) => ({
						...contact,
						contactFields: contact.fields.filter((f) => {
							return (
								f.value.startsWith(currentRecipientAddressInput) ||
								contact.name.toLowerCase().startsWith(currentRecipientAddressInput.toLowerCase())
							);
						})
					}));
			})
		);
	}

	private getRecentContacts(): Observable<MultichainContact[]> {
		return this.contacts$.pipe(
			map((list) => {
				return list
					.filter((item) => item.fields.some((field) => field.last_used))
					.map((contact) => ({
						...contact,
						contactFields: contact.fields.filter((f) => f.last_used)
					}));
			})
		);
	}

	public clearQuery(): void {
		this.query = "";
	}
}
